import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Snackbar,
  Alert,
  Divider,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import AuthContext from '../context/AuthContext';

const ChoresTimeoffPage = () => {
  const { backendUrl, authTokens } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const relationshipId = searchParams.get('relationshipId');

  const [tabValue, setTabValue] = useState(0);
  const [chores, setChores] = useState([]);
  const [timeoffs, setTimeoffs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialog states
  const [choreDialogOpen, setChoreDialogOpen] = useState(false);
  const [timeoffDialogOpen, setTimeoffDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  // Form states
  const [choreName, setChoreName] = useState('');
  const [choreType, setChoreType] = useState(0);
  const [choreWeight, setChoreWeight] = useState(1);
  const [timeoffName, setTimeoffName] = useState('');

  const [updatingHistory, setUpdatingHistory] = useState(false);

  // Delete confirmation dialog states
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Snackbar states
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const fetchChores = async () => {
    try {
      const url = relationshipId
        ? `${backendUrl}api/chores/?relationship_id=${relationshipId}`
        : `${backendUrl}api/chores/`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${authTokens.access}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Filter out default items when we have a relationship ID
        const filteredData = relationshipId
          ? data.filter(chore => chore.user !== null)
          : data;
        setChores(filteredData);
      } else {
        showSnackbar('Failed to fetch chores', 'error');
      }
    } catch (error) {
      showSnackbar('Error fetching chores', 'error');
    }
  };

  const fetchTimeoffs = async () => {
    try {
      const url = relationshipId
        ? `${backendUrl}api/timeoffs/?relationship_id=${relationshipId}`
        : `${backendUrl}api/timeoffs/`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${authTokens.access}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Filter out default items when we have a relationship ID
        const filteredData = relationshipId
          ? data.filter(timeoff => timeoff.user !== null)
          : data;
        setTimeoffs(filteredData);
      } else {
        showSnackbar('Failed to fetch timeoffs', 'error');
      }
    } catch (error) {
      showSnackbar('Error fetching timeoffs', 'error');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchChores(), fetchTimeoffs()]);
      setLoading(false);
    };
    loadData();
  }, [relationshipId]);

  const handleCreateChore = async () => {
    try {
      const payload = {
        name: choreName,
        chore_type: choreType,
        weight: choreWeight,
      };

      if (relationshipId) {
        payload.relationship_id = relationshipId;
      }

      const response = await fetch(`${backendUrl}api/chores/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authTokens.access}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        showSnackbar('Chore created successfully');
        setChoreDialogOpen(false);
        resetChoreForm();
        fetchChores();
      } else {
        const error = await response.json();
        showSnackbar(error.error || 'Failed to create chore', 'error');
      }
    } catch (error) {
      showSnackbar('Error creating chore', 'error');
    }
  };

  const handleEditChore = async () => {
    const weightChanged = choreWeight !== currentItem.weight;
    if (weightChanged) setUpdatingHistory(true);
    setChoreDialogOpen(false);
    try {
      const response = await fetch(`${backendUrl}api/chores/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authTokens.access}`,
        },
        body: JSON.stringify({
          chore_id: currentItem.chore_id,
          name: choreName,
          chore_type: choreType,
          weight: choreWeight,
        }),
      });

      if (response.ok) {
        showSnackbar('Chore updated successfully');
        resetChoreForm();
        fetchChores();
      } else {
        const error = await response.json();
        showSnackbar(error.error || 'Failed to update chore', 'error');
      }
    } catch (error) {
      showSnackbar('Error updating chore', 'error');
    } finally {
      if (weightChanged) setUpdatingHistory(false);
    }
  };

  const handleDeleteChore = async (choreId) => {
    try {
      const response = await fetch(`${backendUrl}api/chores/`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authTokens.access}`,
        },
        body: JSON.stringify({ chore_id: choreId }),
      });

      if (response.ok) {
        showSnackbar('Chore deleted successfully');
        fetchChores();
      } else {
        const error = await response.json();
        showSnackbar(error.error || 'Failed to delete chore', 'error');
      }
    } catch (error) {
      showSnackbar('Error deleting chore', 'error');
    }
  };

  const handleCreateTimeoff = async () => {
    try {
      const payload = {
        name: timeoffName,
      };

      if (relationshipId) {
        payload.relationship_id = relationshipId;
      }

      const response = await fetch(`${backendUrl}api/timeoffs/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authTokens.access}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        showSnackbar('Timeoff created successfully');
        setTimeoffDialogOpen(false);
        resetTimeoffForm();
        fetchTimeoffs();
      } else {
        const error = await response.json();
        showSnackbar(error.error || 'Failed to create timeoff', 'error');
      }
    } catch (error) {
      showSnackbar('Error creating timeoff', 'error');
    }
  };

  const handleEditTimeoff = async () => {
    try {
      const response = await fetch(`${backendUrl}api/timeoffs/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authTokens.access}`,
        },
        body: JSON.stringify({
          timeoff_id: currentItem.timeoff_id,
          name: timeoffName,
        }),
      });

      if (response.ok) {
        showSnackbar('Timeoff updated successfully');
        setTimeoffDialogOpen(false);
        resetTimeoffForm();
        fetchTimeoffs();
      } else {
        const error = await response.json();
        showSnackbar(error.error || 'Failed to update timeoff', 'error');
      }
    } catch (error) {
      showSnackbar('Error updating timeoff', 'error');
    }
  };

  const handleDeleteTimeoff = async (timeoffId) => {
    try {
      const response = await fetch(`${backendUrl}api/timeoffs/`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authTokens.access}`,
        },
        body: JSON.stringify({ timeoff_id: timeoffId }),
      });

      if (response.ok) {
        showSnackbar('Timeoff deleted successfully');
        fetchTimeoffs();
      } else {
        const error = await response.json();
        showSnackbar(error.error || 'Failed to delete timeoff', 'error');
      }
    } catch (error) {
      showSnackbar('Error deleting timeoff', 'error');
    }
  };

  const resetChoreForm = () => {
    setChoreName('');
    setChoreType(0);
    setChoreWeight(1);
    setCurrentItem(null);
    setEditMode(false);
  };

  const resetTimeoffForm = () => {
    setTimeoffName('');
    setCurrentItem(null);
    setEditMode(false);
  };

  const openChoreDialog = (chore = null) => {
    if (chore) {
      setChoreName(chore.name);
      setChoreType(chore.chore_type);
      setChoreWeight(chore.weight);
      setCurrentItem(chore);
      setEditMode(true);
    } else {
      resetChoreForm();
    }
    setChoreDialogOpen(true);
  };

  const openDeleteConfirm = (type, id, name) => {
    setItemToDelete({ type, id, name });
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!itemToDelete) return;
    if (itemToDelete.type === 'chore') {
      handleDeleteChore(itemToDelete.id);
    } else {
      handleDeleteTimeoff(itemToDelete.id);
    }
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  };

  const openTimeoffDialog = (timeoff = null) => {
    if (timeoff) {
      setTimeoffName(timeoff.name);
      setCurrentItem(timeoff);
      setEditMode(true);
    } else {
      resetTimeoffForm();
    }
    setTimeoffDialogOpen(true);
  };

  const getChoreTypeLabel = (type) => {
    switch (type) {
      case 0: return 'Me or partner';
      case 1: return 'Percentage';
      case 2: return 'Hours spent';
      default: return 'Unknown';
    }
  };

  const getItemTypeChip = (item, isChore) => {
    if (item.user === null) {
      return <Chip label="Default" size="small" color="primary" />;
    } else if (item.relationship) {
      return <Chip label="Relationship" size="small" color="secondary" />;
    } else {
      return <Chip label="Personal" size="small" color="default" />;
    }
  };

  return (
    <>
    <SEO title="Chores & Time Off - Couplendar" />
    <Box sx={{
      minHeight: '100vh',
      background: '#F9F7F4',
      py: { xs: 3, md: 4 }
    }}>
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton
            onClick={() => navigate('/relationship')}
            sx={{
              mr: 2,
              backgroundColor: 'white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              '&:hover': {
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              color: '#333'
            }}
          >
            Chores & Timeoff Management
          </Typography>
        </Box>

        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
              background: 'white',
              boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
              borderRadius: 3,
            }}
          >
            <CircularProgress
              size={60}
              sx={{
                color: '#7B6B8D',
              }}
            />
          </Box>
        ) : (
        <Paper
          elevation={0}
          sx={{
            background: 'white',
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
            borderRadius: 3,
            border: 'none',
            overflow: 'hidden'
          }}
        >
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{
              borderBottom: '1px solid rgba(0,0,0,0.08)',
              px: { xs: 2, md: 3 },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500
              }
            }}
          >
            <Tab label="Chores" />
            <Tab label="Timeoff" />
          </Tabs>

        {/* Chores Tab */}
        {tabValue === 0 && (
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>Chores</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => openChoreDialog()}
                sx={{
                  borderRadius: 2,
                  py: 1,
                  px: 2.5,
                  background: '#7B6B8D',
                  boxShadow: 'none',
                  textTransform: 'none',
                  '&:hover': {
                    background: '#6A5A7A',
                    boxShadow: 'none'
                  }
                }}
              >
                Add Chore
              </Button>
            </Box>

            <List sx={{ bgcolor: 'transparent' }}>
              {chores.map((chore) => (
                <React.Fragment key={chore.chore_id}>
                  <ListItem
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      bgcolor: 'rgba(184, 167, 212, 0.03)',
                      '&:hover': {
                        bgcolor: 'rgba(184, 167, 212, 0.08)'
                      }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>{chore.name}</Typography>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">
                            Type: {getChoreTypeLabel(chore.chore_type)} | Weight: {chore.weight}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      {chore.user !== null && (
                        <>
                          <IconButton
                            onClick={() => openChoreDialog(chore)}
                            sx={{
                              color: '#7B6B8D',
                              '&:hover': {
                                bgcolor: 'rgba(184, 167, 212, 0.1)'
                              }
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => openDeleteConfirm('chore', chore.chore_id, chore.name)}
                            sx={{
                              color: '#ff6b6b',
                              '&:hover': {
                                bgcolor: 'rgba(255, 107, 107, 0.1)'
                              }
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Box>
        )}

        {/* Timeoff Tab */}
        {tabValue === 1 && (
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>Time Off</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => openTimeoffDialog()}
                sx={{
                  borderRadius: 2,
                  py: 1,
                  px: 2.5,
                  background: '#7B6B8D',
                  boxShadow: 'none',
                  textTransform: 'none',
                  '&:hover': {
                    background: '#6A5A7A',
                    boxShadow: 'none'
                  }
                }}
              >
                Add Timeoff
              </Button>
            </Box>

            <List sx={{ bgcolor: 'transparent' }}>
              {timeoffs.map((timeoff) => (
                <React.Fragment key={timeoff.timeoff_id}>
                  <ListItem
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      bgcolor: 'rgba(244, 181, 196, 0.03)',
                      '&:hover': {
                        bgcolor: 'rgba(244, 181, 196, 0.08)'
                      }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>{timeoff.name}</Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      {timeoff.user !== null && (
                        <>
                          <IconButton
                            onClick={() => openTimeoffDialog(timeoff)}
                            sx={{
                              color: '#C4856A',
                              '&:hover': {
                                bgcolor: 'rgba(244, 181, 196, 0.1)'
                              }
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => openDeleteConfirm('timeoff', timeoff.timeoff_id, timeoff.name)}
                            sx={{
                              color: '#ff6b6b',
                              '&:hover': {
                                bgcolor: 'rgba(255, 107, 107, 0.1)'
                              }
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Box>
        )}

        {/* Chore Dialog */}
        <Dialog
          open={choreDialogOpen}
          onClose={() => setChoreDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)'
            }
          }}
        >
          <DialogTitle sx={{ pb: 1, fontWeight: 600 }}>
            {editMode ? 'Edit Chore' : 'Create New Chore'}
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Chore Name"
              value={choreName}
              onChange={(e) => setChoreName(e.target.value)}
              margin="normal"
              required
              inputProps={{ maxLength: 100 }}
              helperText={`${choreName.length}/100`}
              sx={{ mt: 1 }}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Chore Type</InputLabel>
              <Select
                value={choreType}
                onChange={(e) => setChoreType(e.target.value)}
                label="Chore Type"
              >
                <MenuItem value={0}>Simple (One person completes it)</MenuItem>
                <MenuItem value={2}>Time-tracked (Track hours for each person)</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Weight</InputLabel>
              <Select
                value={choreWeight}
                onChange={(e) => setChoreWeight(e.target.value)}
                label="Weight"
              >
                {[1, 2, 3, 4, 5].map(n => (
                  <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button
              onClick={() => setChoreDialogOpen(false)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                color: '#666'
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={editMode ? handleEditChore : handleCreateChore}
              variant="contained"
              disabled={!choreName.trim()}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                background: '#7B6B8D',
                boxShadow: 'none',
                '&:hover': {
                  background: '#6A5A7A',
                  boxShadow: 'none'
                }
              }}
            >
              {editMode ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Timeoff Dialog */}
        <Dialog
          open={timeoffDialogOpen}
          onClose={() => setTimeoffDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)'
            }
          }}
        >
          <DialogTitle sx={{ pb: 1, fontWeight: 600 }}>
            {editMode ? 'Edit Timeoff' : 'Create New Timeoff'}
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Timeoff Name"
              value={timeoffName}
              onChange={(e) => setTimeoffName(e.target.value)}
              margin="normal"
              required
              inputProps={{ maxLength: 100 }}
              helperText={`${timeoffName.length}/100`}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button
              onClick={() => setTimeoffDialogOpen(false)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                color: '#666'
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={editMode ? handleEditTimeoff : handleCreateTimeoff}
              variant="contained"
              disabled={!timeoffName.trim()}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                background: '#7B6B8D',
                boxShadow: 'none',
                '&:hover': {
                  background: '#6A5A7A',
                  boxShadow: 'none'
                }
              }}
            >
              {editMode ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        </Paper>
        )}

        {/* Delete confirmation dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)'
            }
          }}
        >
          <DialogTitle sx={{ pb: 1, fontWeight: 600 }}>
            Confirm Delete
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete <strong>{itemToDelete?.name}</strong>? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button
              onClick={() => setDeleteConfirmOpen(false)}
              sx={{ borderRadius: 2, textTransform: 'none', color: '#666' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                background: '#d32f2f',
                boxShadow: 'none',
                '&:hover': {
                  background: '#b71c1c',
                  boxShadow: 'none'
                }
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* History recalculation backdrop */}
        <Backdrop open={updatingHistory} sx={{ color: '#fff', zIndex: 9999, flexDirection: 'column', gap: 2 }}>
          <CircularProgress color="inherit" />
          <Typography variant="h6">Updating history...</Typography>
        </Backdrop>

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
    </>
  );
};

export default ChoresTimeoffPage;